// src/services/pdf/data/computeFlags.ts
// Derives clinical signal flags from GPSummaryData.
// Each flag maps directly to the spec: Section 2.2 — Flag Strip.

import type { GPSummaryData } from "../../../types/gpSummary";
import type { ClinicalFlag } from "../types";

/**
 * Compute the clinical flag strip from aggregated summary data.
 * Flags are auto-generated — no direct user input.
 */
export function computeFlags(data: GPSummaryData): ClinicalFlag[] {
  const flags: ClinicalFlag[] = [];

  // ── Mood declining trend 🔴 ───────────────────────────────────────────────
  // Trigger: Avg mood W5–W6 < avg mood W1–W2 by ≥0.5 points
  if (data.mood.length >= 4) {
    const earlyWeeks = data.mood.slice(0, 2);
    const lateWeeks = data.mood.slice(-2);
    const earlyAvg = earlyWeeks.reduce((s, m) => s + m.score, 0) / earlyWeeks.length;
    const lateAvg = lateWeeks.reduce((s, m) => s + m.score, 0) / lateWeeks.length;

    if (earlyAvg - lateAvg >= 0.5) {
      flags.push({
        level: "red",
        label: "Mood declining trend",
        detail: `W1-W2 avg ${earlyAvg.toFixed(1)} -> W5-W6 avg ${lateAvg.toFixed(1)}`,
      });
    }
  }

  // ── Bladder leakage ongoing 🔴 ────────────────────────────────────────────
  // Trigger: Bladder symptom reported in Week 5 OR Week 6
  const bladderSymptoms = data.symptoms.find(
    (s) => s.name === "Bladder Control" && (s.status === "worsening" || s.status === "stable")
  );
  if (bladderSymptoms && bladderSymptoms.severity !== "none") {
    flags.push({
      level: "red",
      label: "Bladder leakage ongoing",
      detail: `Status: ${bladderSymptoms.status}, current severity: ${bladderSymptoms.severity}`,
    });
  }

  // ── GDM — HbA1c due 🟠 ────────────────────────────────────────────────────
  // This would require FTU.A.7 data — flagged if GDM in history
  // For now, we derive what we can from existing data

  // ── Lochia resolved 🟢 ────────────────────────────────────────────────────
  // Trigger: No bleeding logged for ≥7 consecutive days
  const bleedingDays = data.bleedingLog;
  let maxNoBleedStreak = 0;
  let currentStreak = 0;
  for (const day of bleedingDays) {
    if (day.flow === "none") {
      currentStreak++;
      maxNoBleedStreak = Math.max(maxNoBleedStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }

  if (maxNoBleedStreak >= 7) {
    flags.push({
      level: "green",
      label: "Lochia resolved",
      detail: `>=7 consecutive days without bleeding`,
    });
  } else if (bleedingDays.some((d) => d.flow !== "none" && d.week >= 5)) {
    flags.push({
      level: "amber",
      label: "Bleeding still present in later weeks",
      detail: "Lochia not yet resolved",
    });
  }

  // ── 0 red flags in 6 weeks 🟢 ────────────────────────────────────────────
  if (data.redFlags.length === 0) {
    flags.push({
      level: "green",
      label: "0 red flags in 6 weeks",
      detail: "No symptom alerts triggered during the programme",
    });
  } else {
    flags.push({
      level: "amber",
      label: `${data.redFlags.length} red flag${data.redFlags.length > 1 ? "s" : ""} triggered`,
      detail: data.redFlags.slice(0, 2).map((f) => f.label).join("; "),
    });
  }

  // ── Symptoms worsening 🔴 ─────────────────────────────────────────────────
  const worsening = data.symptoms.filter((s) => s.status === "worsening");
  if (worsening.length > 0) {
    flags.push({
      level: "red",
      label: `${worsening.length} symptom${worsening.length > 1 ? "s" : ""} worsening`,
      detail: worsening.map((s) => s.name).join(", "),
    });
  }

  return flags;
}
